/**
 * cm-coding: Operators expect Sync, Resync all, and Unsync to update managed
 * rows, connector cards, and completion feedback as one action. This hook runs
 * that provider-neutral job lifecycle after the Manage dialog supplies a batch.
 * The backend exposes per-record endpoints, so one selection starts one job per
 * record; keep their pending state, refresh, and final toast together so a
 * ten-record selection does not appear as ten unrelated operations.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/services'
import { waitForVaultJob } from '@/subsystems/vault/services/vault-jobs'

import {
  refreshSyncedConnectorFeed,
  syncManagedConnectorRecord,
  unsyncConnectorRecord,
} from '../api'
import type {
  ConnectorManagedRecord,
  ConnectorPendingAction,
  ConnectorPullSource,
} from '../types'

const withKeys = (current: Set<string>, keys: string[]) => new Set([...current, ...keys])

const withoutKeys = (current: Set<string>, keys: string[]) => {
  const next = new Set(current)
  keys.forEach(key => next.delete(key))
  return next
}

export function useManagedRecordActions({
  api,
  orgId,
  workspaceId,
  vaultId,
  source,
  records,
  managedKey,
  recordUnit,
  onQueued,
  onPendingChange,
}: {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  vaultId?: string
  source?: ConnectorPullSource
  records: ConnectorManagedRecord[]
  managedKey: readonly unknown[]
  recordUnit: { singular: string; plural: string }
  onQueued?: () => void | Promise<unknown>
  onPendingChange?: (feedId: string, action?: ConnectorPendingAction) => void
}) {
  const queryClient = useQueryClient()
  const [removing, setRemoving] = useState<Set<string>>(new Set())
  const [syncing, setSyncing] = useState<Set<string>>(new Set())
  const [resyncing, setResyncing] = useState(false)

  const run = async (
    action: ConnectorPendingAction,
    operations: Array<() => Promise<{ jobId: string }>>,
    refreshManaged = true,
  ) => {
    if (!source) return
    onPendingChange?.(source.feedId, action)
    try {
      const jobs: { jobId: string }[] = []
      for (const operation of operations) jobs.push(await operation())
      if (refreshManaged) await queryClient.invalidateQueries({ queryKey: managedKey })
      await onQueued?.()
      await Promise.all(jobs.map(job => waitForVaultJob(api, orgId, workspaceId, job.jobId)))
      if (refreshManaged) await queryClient.invalidateQueries({ queryKey: managedKey })
      await onQueued?.()
    } finally {
      onPendingChange?.(source.feedId)
    }
  }

  const syncRecords = async (selected: ConnectorManagedRecord[]) => {
    if (!source || !vaultId || selected.length === 0) return
    const keys = selected.map(record => record.recordKey)
    setSyncing(current => withKeys(current, keys))
    try {
      await run('sync', selected.map(record => () => syncManagedConnectorRecord(
        api,
        orgId,
        vaultId,
        source.feedId,
        record.recordKey,
      )))
      toast.success(selected.length === 1
        ? `${selected[0].identifier} synced`
        : `Synced ${selected.length} ${recordUnit.plural}`)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? `Could not sync ${selected.length === 1 ? recordUnit.singular : recordUnit.plural}`)
    } finally {
      setSyncing(current => withoutKeys(current, keys))
    }
  }

  const resyncAll = async () => {
    if (!source || !vaultId || records.length === 0) return
    setResyncing(true)
    try {
      await run('sync', [() => refreshSyncedConnectorFeed(
        api,
        orgId,
        vaultId,
        source.feedId,
      )])
      toast.success(`Resynced ${records.length} ${records.length === 1 ? recordUnit.singular : recordUnit.plural}`)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? `Could not resync ${recordUnit.plural}`)
    } finally {
      setResyncing(false)
    }
  }

  const unsyncRecords = async (selected: ConnectorManagedRecord[]) => {
    if (!source || !vaultId || selected.length === 0) return
    const keys = selected.map(record => record.recordKey)
    const keySet = new Set(keys)
    const previous = queryClient.getQueryData<ConnectorManagedRecord[]>(managedKey)
    setRemoving(current => withKeys(current, keys))
    queryClient.setQueryData<ConnectorManagedRecord[]>(
      managedKey,
      current => current?.filter(item => !keySet.has(item.recordKey)),
    )
    try {
      // cm-coding: Unsync hides the rows before the deletion jobs finish so
      // users cannot enqueue the same destructive action twice. Keep that
      // optimistic cache authoritative; an eager refetch can still see the
      // source projection and resurrect the rows during asynchronous cleanup.
      await run(
        'remove',
        selected.map(record => () => unsyncConnectorRecord(
          api,
          orgId,
          vaultId,
          source.feedId,
          record.recordKey,
        )),
        false,
      )
      toast.success(selected.length === 1
        ? `${selected[0].identifier} is no longer syncing`
        : `${selected.length} ${recordUnit.plural} are no longer syncing`)
    } catch (error: any) {
      queryClient.setQueryData(managedKey, previous)
      toast.error(error?.response?.data?.detail ?? error?.message ?? `Could not unsync ${selected.length === 1 ? recordUnit.singular : recordUnit.plural}`)
    } finally {
      setRemoving(current => withoutKeys(current, keys))
    }
  }

  return { removing, syncing, resyncing, syncRecords, resyncAll, unsyncRecords }
}
